// Regole dell'agenda della SPA, prese dal prototipo (prototipo/agenda.html) e dal foglio Excel
// (docs/REGOLE-PLANNING.md). Funzioni pure: ricevono i dati del giorno e rispondono, senza pagina né database.
//
// Forma dei dati:
//  prenotazione: { id, data: 'AAAA-MM-GG', inizio: 'HH:MM', durata (minuti), tr (id listino), sala, lettino (1|2),
//                  op ('op1'…), camera, nome, tipo, importo, listino (prezzo del menù), motivo, motivoSconto,
//                  magg (sì/no), coppia (id o null), stato, addebito, addebitoNoShow (solo no-show: lo scrive la responsabile),
//                  est: { tel, email, paga, caparra } | null }
//  giorno:       { prenotazioni: [...], turni: { op1: true, … } | null, pause: { op1: '12:00', … }, blocchi: [...] }
//  blocco (S8):  { id, data, sala, lettino (0 = tutti e 2), inizio, fine, motivo, tolto }
// I testi dei controlli stanno nelle lingue (lingue/it.js, en.js): qui solo le chiavi.
import { TRATTAMENTI } from './listino.js';
import { t } from '../i18n.js';

export const START = 630; // 10:30
export const END = 1140; // 19:00
export const STEP = 15;
export const RIORDINO = 15; // minuti di riordino tra due trattamenti sullo stesso lettino
export const PAUSA = 60; // pausa pranzo (P.P): un'ora

export const SALE = Object.freeze([
  { id: 'flow', nome: 'Flow' },
  { id: 'aura', nome: 'Aura' },
  { id: 'harmonia', nome: 'Harmonia' },
]);
export const LETTINI = Object.freeze([1, 2]);
// I nomi veri delle operatrici si scrivono nell'app: qui solo i codici.
export const OPERATRICI = Object.freeze(['op1', 'op2', 'op3']);
export const TIPI = Object.freeze(['pagamento', 'voucher', 'complimentary', 'proprieta']);
export const PAGA_ESTERNO = Object.freeze(['pos', 'contanti', 'dadecidere']);
export const STATI = Object.freeze(['confermata', 'arrivato', 'fatto', 'disdetta', 'no-show']);

// ---------- orari ----------
const pad = (n) => String(n).padStart(2, '0');
export const toMin = (hhmm) => { const [h, m] = String(hhmm).split(':').map(Number); return h * 60 + m; };
/** 630 → '10:30' (con lo zero davanti: per i valori salvati). */
export const hhmm = (m) => `${pad(Math.floor(m / 60))}:${pad(m % 60)}`;
/** 630 → '10:30', 540 → '9:00' (da leggere). */
export const hm = (m) => `${Math.floor(m / 60)}:${pad(m % 60)}`;
export const fine = (b) => toMin(b.inizio) + b.durata;
/** Una prenotazione tiene occupati lettino e operatrice, tranne se disdetta o no-show. */
export const occupa = (b) => b.stato !== 'disdetta' && b.stato !== 'no-show';
export const esterno = (camera) => /^est/i.test(String(camera || '').trim());
export const giornoBreve = (k) => { const [, m, d] = String(k).split('-').map(Number); return `${d}/${m}`; };
export const salaNome = (id) => (SALE.find((s) => s.id === id) || {}).nome || id;
export const nomeOperatrice = (op, nomi = {}) => nomi[op] || t('op.n', { n: OPERATRICI.indexOf(op) + 1 || op });
const lettTxt = (x) => (+x.lettino ? t('ctrl.lettinoN', { n: x.lettino }) : t('ctrl.tuttiLettini'));
/** Numero scritto a mano: «130,50» → 130.5; vuoto o sbagliato → 0. */
export const numero = (v) => { const n = parseFloat(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : 0; };
/** Orari che si possono scegliere: dalle 10:30 alle 18:45, ogni 15'. */
export function orari() {
  const out = [];
  for (let m = START; m < END; m += STEP) out.push(hhmm(m));
  return out;
}
/** Orari della P.P (pausa pranzo di un'ora): dalle 11:00 alle 15:00, ogni 30' (come nel prototipo). */
export const ORARI_PAUSA = Object.freeze([660, 690, 720, 750, 780, 810, 840, 870, 900].map(hhmm));

/** Data e ora di adesso in Italia: { data: 'AAAA-MM-GG', minuti }. Il telefono può avere un altro fuso. */
export function adessoRoma(ora = new Date()) {
  const data = ora.toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' });
  const [hh, mm] = ora.toLocaleTimeString('en-GB', { timeZone: 'Europe/Rome', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .split(':').map(Number);
  return { data, minuti: hh * 60 + mm };
}

// ---------- turni, pause, sale fuori servizio ----------
/** Senza turni scritti per quel giorno, tutte sono in turno (come nel prototipo). */
export const inTurno = (giorno, op) => (giorno?.turni ? !!giorno.turni[op] : true);
export function pausa(giorno, op) {
  const p = giorno?.pause?.[op];
  return p ? { s: toMin(p), e: toMin(p) + PAUSA } : null;
}
export const blocchiDel = (giorno, data, sala) => (giorno?.blocchi || [])
  .filter((x) => !x.tolto && (!data || !x.data || x.data === data) && (!sala || x.sala === sala));

// ---------- soldi ----------
/**
 * Incasso come nel foglio: conta solo ciò che si paga; disdetta tardiva = addebito pieno, gratuita = 0.
 * No-show: conta solo l'importo scritto dalla responsabile (finché non lo scrive, 0).
 */
export function incasso(b) {
  if (b.tipo !== 'pagamento') return 0;
  if (b.stato === 'disdetta') return b.addebito ? (b.importo || 0) : 0;
  if (b.stato === 'no-show') return Math.min(b.addebitoNoShow || 0, b.importo || 0);
  return b.importo || 0;
}
/** No-show di un ospite che paga, con l'importo da addebitare non ancora scritto dalla responsabile. */
export const noShowDaDecidere = (b) => b.stato === 'no-show' && b.tipo === 'pagamento' && b.importo > 0
  && (b.addebitoNoShow === undefined || b.addebitoNoShow === null);
/** Importo del no-show scritto a mano: da 0 al totale della prenotazione, al massimo due decimali. Vuoto o sbagliato → null. */
export function importoNoShow(testo, b) {
  const s = String(testo ?? '').trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const n = Math.round(Number(s) * 100) / 100;
  return n <= (b.importo || 0) ? n : null;
}
/** Valore offerto (complimentary) che non è stato disdetto. */
export const valoreCompl = (b) => (b.tipo === 'complimentary' && occupa(b) ? (b.importo || 0) : 0);

/**
 * Sotto il listino (regola di Mario #06: il listino è la base): prenotazione a pagamento con importo più basso
 * del menù, o durata più corta. Serve il motivo dello sconto.
 */
export function ridotto(candidati, indice = TRATTAMENTI) {
  if (!candidati.length || candidati[0].tipo !== 'pagamento') return false;
  return candidati.some((c) => {
    const t = indice[c.tr];
    const listino = c.listino !== undefined ? c.listino : t?.prezzo;
    return (listino != null && c.importo < listino) || (t && c.durata < t.durata);
  });
}

// ---------- controlli automatici ----------
/**
 * Problemi che bloccano una prenotazione: fuori orario, fuori turno, P.P, sala fuori servizio,
 * lettino occupato (più i 15' di riordino), operatrice già impegnata.
 * @param {object} c prenotazione candidata
 * @param {object} giorno dati del giorno (prenotazioni, turni, pause, blocchi)
 * @param {{ ignora?: string[], nomi?: object }} [opz] ignora = id da saltare (la prenotazione che si sta modificando)
 * @returns {string[]}
 */
export function problemi(c, giorno, { ignora = [], nomi = {} } = {}) {
  const out = [];
  const s = toMin(c.inizio);
  const e = s + c.durata;
  const opN = nomeOperatrice(c.op, nomi);
  if (s < START || e > END) out.push(t('ctrl.fuoriOrario', { da: hm(START), a: hm(END), fine: hm(e) }));
  if (!inTurno(giorno, c.op)) out.push(t('ctrl.fuoriTurno', { op: opN, giorno: giornoBreve(c.data) }));
  const p = pausa(giorno, c.op);
  if (p && s < p.e && p.s < e) out.push(t('ctrl.pausa', { op: opN, da: hm(p.s), a: hm(p.e) }));
  for (const x of blocchiDel(giorno, c.data, c.sala)) {
    if ((!+x.lettino || +x.lettino === +c.lettino) && s < toMin(x.fine) && toMin(x.inizio) < e) {
      out.push(t('ctrl.fuoriServizio', {
        sala: salaNome(x.sala), lettini: lettTxt(x), da: hm(toMin(x.inizio)), a: hm(toMin(x.fine)), motivo: x.motivo,
      }));
    }
  }
  for (const o of giorno?.prenotazioni || []) {
    if (ignora.includes(o.id) || !occupa(o) || (o.data && c.data && o.data !== c.data)) continue;
    const os = toMin(o.inizio);
    const oe = fine(o);
    if (o.sala === c.sala && +o.lettino === +c.lettino && s < oe + RIORDINO && os < e + RIORDINO) {
      out.push(t('ctrl.lettinoOccupato', {
        sala: salaNome(o.sala), lettino: o.lettino, nome: o.nome || t('ctrl.unOspite'), da: hm(os), a: hm(oe), riordino: RIORDINO,
      }));
    }
    if (o.op === c.op && s < oe && os < e) {
      out.push(t('ctrl.impegnata', { op: opN, nome: o.nome || t('ctrl.unOspite'), da: hm(os), a: hm(oe) }));
    }
  }
  return out;
}

export const eur = (n) => `${new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(n || 0)} €`;

/**
 * Tutti i controlli prima di salvare una prenotazione (una persona, o due per la coppia).
 * @param {object[]} candidati 1 o 2 prenotazioni (la coppia: lettino 1 e 2 della stessa sala)
 * @param {{ giorno: object, ignora?: string[], nomi?: object, indice?: object, inCoppia?: boolean, salute?: object }} contesto
 *   inCoppia: la prenotazione modificata fa già parte di una coppia · salute: { grav } solo per l'avviso
 * @returns {{ errori: string[], avvisi: string[] }} errori = non si salva · avvisi = si salva, ma attenzione
 */
export function controllaPrenotazione(candidati, contesto = {}) {
  const { giorno = {}, ignora = [], nomi = {}, indice = TRATTAMENTI, inCoppia = false, salute = null } = contesto;
  const cs = candidati;
  const errori = [];
  const avvisi = [];
  const c0 = cs[0] || {};
  const doppia = cs.length === 2;
  const chi = (i) => (doppia ? t('ctrl.persona', { n: i + 1 }) : '');

  if (!c0.data) errori.push(t('ctrl.giorno'));
  if (!String(c0.nome || '').trim()) errori.push(t('ctrl.nome'));
  if (!c0.magg) errori.push(t('ctrl.magg'));
  if (c0.tipo === 'complimentary' && !String(c0.motivo || '').trim()) errori.push(t('ctrl.motivoCompl'));
  if (doppia && c0.op === cs[1].op) errori.push(t('ctrl.coppiaOp'));
  if (doppia && (c0.sala !== cs[1].sala || +c0.lettino === +cs[1].lettino)) errori.push(t('ctrl.coppiaSala'));
  if (!(Number.isInteger(c0.durata) && c0.durata >= 15 && c0.durata <= 240)) errori.push(t('ctrl.durata'));
  if (cs.some((c) => !(c.importo >= 0))) errori.push(t('ctrl.importo'));
  if (ridotto(cs, indice) && !String(c0.motivoSconto || '').trim()) errori.push(t('ctrl.motivoSconto'));
  if (doppia || inCoppia) {
    cs.forEach((c) => {
      const tr = indice[c.tr];
      if (tr?.solo) errori.push(t('ctrl.solo', { tr: tr.nome }));
    });
  }
  cs.forEach((c) => { if (!indice[c.tr]) errori.push(t('ctrl.trattamento')); });
  if (c0.data) cs.forEach((c, i) => problemi(c, giorno, { ignora, nomi }).forEach((p) => errori.push(chi(i) + p)));

  cs.forEach((c, i) => {
    const tr = indice[c.tr];
    if (!tr) return;
    if (c.tipo === 'pagamento' && tr.prezzo == null) avvisi.push(t('ctrl.prezzoIgnoto', { chi: chi(i), tr: tr.nome }));
    else if (c.tipo === 'pagamento' && !c.importo) avvisi.push(t('ctrl.importoZero', { chi: chi(i) }));
    if (tr.durataDaConf) avvisi.push(t('ctrl.durataDaConf', { chi: chi(i), tr: tr.nome, durata: tr.durata }));
    if (tr.sala && tr.sala !== c.sala) avvisi.push(t('ctrl.salaSolita', { tr: tr.nome, sala: salaNome(tr.sala) }));
  });
  if (!String(c0.camera || '').trim()) avvisi.push(t('ctrl.camera'));
  const ex = c0.est;
  if (ex) {
    if (!ex.tel && !ex.email) avvisi.push(t('ctrl.estContatti'));
    if (ex.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(ex.email)) avvisi.push(t('ctrl.email'));
    if (ex.caparra < 0) errori.push(t('ctrl.caparraNeg'));
    const tot = cs.reduce((x, c) => x + (c.tipo === 'pagamento' ? (c.importo || 0) : 0), 0);
    if (ex.caparra > tot && c0.tipo === 'pagamento') avvisi.push(t('ctrl.caparraAlta', { caparra: eur(ex.caparra), totale: eur(tot) }));
    if (c0.tipo === 'proprieta') avvisi.push(t('ctrl.estProprieta'));
  }
  if (salute?.grav) avvisi.push(t('ctrl.grav'));
  return { errori, avvisi };
}

/**
 * S5: il primo orario dove tutto è libero (lettino, sala in servizio, operatrice in turno, fuori dalla P.P).
 * Prova prima la sala e l'operatrice scelte, poi le altre. Oggi parte dall'ora attuale (arrotondata ai 15').
 * @param {object[]} candidati 1 o 2 prenotazioni (orario, sala, lettino e operatrice vengono cambiati)
 * @param {object} giorno dati del giorno
 * @param {{ ignora?: string[], adesso?: { data: string, minuti: number } }} [opz]
 * @returns {object[] | null} candidati sistemati; [] se non c'è posto; null se manca il giorno
 */
export function primoLibero(candidati, giorno, { ignora = [], adesso = null } = {}) {
  const cs = candidati;
  const d = cs[0]?.data;
  if (!d) return null;
  const da = adesso && adesso.data === d ? Math.ceil(adesso.minuti / STEP) * STEP : START;
  const sale = [cs[0].sala, ...SALE.map((x) => x.id).filter((x) => x !== cs[0].sala)];
  const opsDi = (sel) => [sel, ...OPERATRICI.filter((o) => o !== sel)].filter((o) => inTurno(giorno, o));
  const durMax = Math.max(...cs.map((c) => c.durata));
  const libero = (c) => problemi(c, giorno, { ignora }).length === 0;
  for (let m = Math.max(START, da); m + durMax <= END; m += STEP) {
    for (const sala of sale) {
      for (const lett of cs.length === 2 ? [1] : LETTINI) {
        for (const op1 of opsDi(cs[0].op)) {
          const c1 = { ...cs[0], inizio: hhmm(m), sala, lettino: lett, op: op1 };
          if (!libero(c1)) continue;
          if (cs.length === 1) return [c1];
          for (const op2 of opsDi(cs[1].op).filter((o) => o !== op1)) {
            const c2 = { ...cs[1], inizio: hhmm(m), sala, lettino: 2, op: op2 };
            if (libero(c2)) return [c1, c2];
          }
        }
      }
    }
  }
  return [];
}

/** Prenotazioni del giorno divise per colonna «sala-lettino» (6 colonne), in ordine di orario. */
export function colonneDelGiorno(prenotazioni) {
  const col = {};
  for (const s of SALE) for (const l of LETTINI) col[`${s.id}-${l}`] = [];
  for (const b of prenotazioni) {
    const k = `${b.sala}-${+b.lettino}`;
    if (col[k]) col[k].push(b);
  }
  for (const k of Object.keys(col)) col[k].sort((a, b) => toMin(a.inizio) - toMin(b.inizio));
  return col;
}

/**
 * Spazi liberi di un lettino, per il tocco sull'agenda: tra le 10:30 e le 19:00, tolti i trattamenti
 * (con i 15' di riordino prima e dopo) e i fuori servizio (S8). L'operatrice si controlla poi nel modulo.
 * @returns {{ inizio: string, fine: string, minuti: number }[]} solo gli spazi di almeno `minimo` minuti
 */
export function spaziLiberi(giorno, sala, lettino, { minimo = 30 } = {}) {
  const pieni = [];
  for (const b of giorno?.prenotazioni || []) {
    if (occupa(b) && b.sala === sala && +b.lettino === +lettino) pieni.push([toMin(b.inizio) - RIORDINO, fine(b) + RIORDINO]);
  }
  for (const x of blocchiDel(giorno, null, sala)) {
    if (!+x.lettino || +x.lettino === +lettino) pieni.push([toMin(x.inizio), toMin(x.fine)]);
  }
  pieni.sort((a, b) => a[0] - b[0]);
  const out = [];
  let da = START;
  const aggiungi = (a) => { if (a - da >= minimo) out.push({ inizio: hhmm(da), fine: hhmm(a), minuti: a - da }); };
  for (const [s, e] of pieni) {
    if (s > da) aggiungi(Math.min(s, END));
    da = Math.max(da, e);
    if (da >= END) break;
  }
  if (da < END) aggiungi(END);
  return out;
}

/** Ore che mancano all'inizio del trattamento (negative se è già passato). `adesso` come da adessoRoma(). */
export function oreMancanti(b, adesso) {
  const giorni = (k) => { const [y, m, d] = String(k).split('-').map(Number); return Date.UTC(y, m - 1, d) / 864e5; };
  return (giorni(b.data) - giorni(adesso.data)) * 24 + (toMin(b.inizio) - adesso.minuti) / 60;
}

/**
 * Disdetta (regola del menù): gratis fino a 24 ore prima; dopo, addebito dell'intero importo (se si paga).
 * @returns {{ ore: number, tardiva: boolean, pagante: boolean, addebito: boolean }}
 */
export function esitoDisdetta(b, adesso) {
  const ore = oreMancanti(b, adesso);
  const tardiva = ore < 24;
  const pagante = b.tipo === 'pagamento' && b.importo > 0;
  return { ore, tardiva, pagante, addebito: tardiva && pagante };
}

/**
 * Cambi di stato che il telefono può fare su quella prenotazione (come le regole di sicurezza):
 * ricevimento, responsabile e sviluppo tutti; l'operatrice solo sulle sue e mai la disdetta.
 * @param {{ prenota: boolean, op?: string|null }} chi prenota = può creare e modificare · op = colonna dell'operatrice
 * @returns {string[]} stati di arrivo, in ordine
 */
export function azioniStato(b, { prenota = false, op = null } = {}) {
  const sua = !!op && b.op === op;
  if (!prenota && !sua) return [];
  if (b.stato === 'confermata') return prenota ? ['arrivato', 'no-show', 'disdetta'] : ['arrivato', 'no-show'];
  if (b.stato === 'arrivato') return ['fatto', 'no-show'];
  if (b.stato === 'disdetta') return prenota ? ['confermata'] : [];
  return ['confermata'];
}

/**
 * Prenotazioni del giorno che non vanno più bene (es. dopo un cambio di turno o di P.P).
 * @returns {{ prenotazione: object, problemi: string[] }[]}
 */
export function conflittiDelGiorno(giorno, { nomi = {} } = {}) {
  return (giorno?.prenotazioni || [])
    .filter(occupa)
    .map((b) => ({ prenotazione: b, problemi: problemi(b, giorno, { ignora: [b.id], nomi }) }))
    .filter((x) => x.problemi.length);
}
